import type { ComponentChildren, JSX } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { languageName, TRANSCRIPT_LANGUAGES } from '../../../transcribe/languages.js';
import { api, type AuditItem, type ChannelInfo, type ClipItem, type FollowMode, type GuildData, type Me, type RecordingItem, type Settings } from './api.js';
import {
  Card,
  Field,
  fmtAgo,
  fmtDate,
  fmtDuration,
  GuildIcon,
  Link,
  Notice,
  NumberInput,
  People,
  PlanBadge,
  plural,
  PremiumBadge,
  Spinner,
  Toggle,
  useToast,
} from './ui.js';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'behavior', label: 'Behavior' },
  { id: 'recording', label: 'Recording & clips' },
  { id: 'library', label: 'Library' },
  { id: 'premium', label: 'Premium' },
  { id: 'activity', label: 'Activity' },
] as const;

const FOLLOW_MODES: { id: FollowMode; title: string; hint: string }[] = [
  { id: 'auto', title: 'Follow people anywhere', hint: 'Joins the first voice channel someone enters and leaves when it empties.' },
  { id: 'allowlist', title: 'Only allowed channels', hint: 'Joins on its own only in the channels you allow below. /join still works elsewhere.' },
  { id: 'manual', title: 'Only when asked', hint: 'Never joins on its own; someone has to use /join or /move.' },
];

const FOLLOW_SHORT: Record<FollowMode, string> = {
  auto: 'Follows people into any voice channel',
  allowlist: 'Follows people into allowed channels only',
  manual: 'Only when someone uses /join or /move',
};

type SetData = (d: GuildData) => void;

interface TabProps {
  data: GuildData;
  setData: SetData;
  reload: () => Promise<void>;
  base: string;
}

export function GuildPage(props: { guildId: string; tab: string; me: Me }) {
  const [data, setData] = useState<GuildData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setData(await api.guild(props.guildId));
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [props.guildId]);
  useEffect(() => void load(), [load]);

  if (!data) {
    return error ? (
      <div class="stack">
        <Notice kind="error">{error}</Notice>
        <Link href="/dashboard">← Back to your servers</Link>
      </div>
    ) : (
      <Spinner />
    );
  }

  const base = `/dashboard/${data.guild.id}`;
  const tab = TABS.some((t) => t.id === props.tab) ? props.tab : 'overview';
  const tabProps: TabProps = { data, setData, reload: load, base };
  return (
    <>
      <div class="guild-head">
        <Link href="/dashboard" class="back" aria-label="All servers">
          ←
        </Link>
        <GuildIcon name={data.guild.name} iconUrl={data.guild.iconUrl} size={44} />
        <div class="guild-title">
          <h1>{data.guild.name}</h1>
          <PlanBadge plan={data.plan.id} />
        </div>
      </div>
      <nav class="tabs" aria-label="Server settings">
        {TABS.map((t) => (
          <Link key={t.id} href={t.id === 'overview' ? base : `${base}/${t.id}`} class="tab" aria-current={t.id === tab ? 'page' : undefined}>
            {t.label}
          </Link>
        ))}
      </nav>
      {tab === 'overview' && <Overview {...tabProps} />}
      {tab === 'behavior' && <Behavior {...tabProps} />}
      {tab === 'recording' && <RecordingTab {...tabProps} />}
      {tab === 'library' && <Library {...tabProps} />}
      {tab === 'premium' && <PremiumTab {...tabProps} />}
      {tab === 'activity' && <Activity {...tabProps} />}
    </>
  );
}

function channelName(data: GuildData, id: string | null): string {
  if (!id) return 'a channel';
  return [...data.guild.voiceChannels, ...data.guild.textChannels].find((c) => c.id === id)?.name ?? 'a deleted channel';
}

/** Runs a change, swaps in the server's answer, and reports success or the error. */
function useSaver(setData: SetData) {
  const [error, setError] = useState<string | null>(null);
  const [toast, showToast] = useToast();
  const [busy, setBusy] = useState(false);
  const save = async (fn: () => Promise<GuildData>, message = 'Saved') => {
    setError(null);
    setBusy(true);
    try {
      setData(await fn());
      showToast(message);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return { save, error, toast, busy };
}

function Status(props: { error: string | null; toast: string | null }) {
  return (
    <>
      {props.error && <Notice kind="error">{props.error}</Notice>}
      <div class="toast" aria-live="polite">
        {props.toast && <span>✓ {props.toast}</span>}
      </div>
    </>
  );
}

// ---- Overview ----

function Overview({ data, setData, reload, base }: TabProps) {
  const { save, error, toast, busy } = useSaver(setData);
  // Keep "Live now" fresh while this tab is open.
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') void reload();
    }, 15_000);
    return () => clearInterval(t);
  }, [reload]);
  const s = data.effective;
  const plan = data.plan;
  const blocked = data.rules.block.map((id) => `#${channelName(data, id)}`);
  const roles = data.guild.roles.filter((r) => data.excludedRoles.includes(r.id)).map((r) => `@${r.name}`);

  return (
    <div class="stack">
      <Card
        title="Live now"
        actions={
          <button type="button" class="btn ghost small" onClick={() => void reload()}>
            Refresh
          </button>
        }
      >
        {data.live.length === 0 ? (
          <p class="muted">No listener is in a call right now. {FOLLOW_SHORT[s.followMode]}.</p>
        ) : (
          <ul class="live-list">
            {data.live.map((l) => (
              <li key={l.botId} class="live">
                <div class="live-main">
                  <div class="live-title">
                    <span class={`state-dot ${l.paused ? 'paused' : 'on'}`} aria-hidden="true" />
                    {l.botName} in <strong>#{channelName(data, l.channelId)}</strong>
                  </div>
                  <div class="muted small">
                    {l.paused ? 'Paused' : 'Listening'} for {fmtDuration(Date.now() - l.startedAt)} · {plural(l.speakers, 'speaker')} in the replay buffer
                    {l.cameras ? ` · ${plural(l.cameras, 'camera')}` : ''}
                  </div>
                  {l.recording && (
                    <div class="rec-line">
                      ⏺ Recording for {fmtDuration(l.recording.elapsedMs)} · {plural(l.recording.speakers, 'speaker')}
                    </div>
                  )}
                </div>
                <div class="row-actions">
                  {l.recording && (
                    <button type="button" class="btn small" disabled={busy} onClick={() => save(() => api.stopRecording(data.guild.id, l.botId), 'Recording stopped')}>
                      Stop recording
                    </button>
                  )}
                  <button
                    type="button"
                    class="btn small danger"
                    disabled={busy}
                    onClick={() => {
                      if (confirm(`Make ${l.botName} leave? It won't come back to that channel until everyone has left it.`)) {
                        void save(() => api.leave(data.guild.id, l.botId), 'Left the channel');
                      }
                    }}
                  >
                    Make it leave
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <Status error={error} toast={toast} />
      </Card>

      <div class="grid-2">
        <Card
          title="How it behaves"
          actions={
            <Link href={`${base}/behavior`} class="btn ghost small">
              Change
            </Link>
          }
        >
          <dl class="facts">
            <dt>Joins</dt>
            <dd>
              {FOLLOW_SHORT[s.followMode]}
              {s.minHumans > 1 ? `, once ${s.minHumans} people are in` : ''}
            </dd>
            <dt>Never joins</dt>
            <dd>{blocked.length ? blocked.join(', ') : 'No blocked channels'}</dd>
            <dt>Notices</dt>
            <dd>{s.announceChannelId ? `#${channelName(data, s.announceChannelId)}` : "Each voice channel's chat"}</dd>
            <dt>Never captured</dt>
            <dd>{roles.length ? roles.join(', ') : 'Only people who used /optout'}</dd>
          </dl>
        </Card>
        <Card
          title={`${plan.name} plan`}
          actions={
            <Link href={`${base}/premium`} class="btn ghost small">
              {plan.id === 'premium' ? 'Details' : 'Upgrade'}
            </Link>
          }
        >
          <dl class="facts">
            <dt>Channels at once</dt>
            <dd>{plan.maxListeners}</dd>
            <dt>Instant replay</dt>
            <dd>{s.bufferSeconds}s</dd>
            <dt>Longest recording</dt>
            <dd>{s.maxRecordMinutes} min</dd>
            <dt>Kept for</dt>
            <dd>{plural(s.retentionDays, 'day')}</dd>
          </dl>
        </Card>
      </div>
    </div>
  );
}

// ---- Behavior ----

function groupByCategory(channels: ChannelInfo[]): [string | null, ChannelInfo[]][] {
  const groups: [string | null, ChannelInfo[]][] = [];
  for (const c of channels) {
    const last = groups[groups.length - 1];
    if (last && last[0] === c.category) last[1].push(c);
    else groups.push([c.category, [c]]);
  }
  return groups;
}

function Behavior({ data, setData, base }: TabProps) {
  const { save, error, toast } = useSaver(setData);
  const id = data.guild.id;
  const s = data.settings;
  const plan = data.plan;
  const block = new Set(data.rules.block);
  const allow = new Set(data.rules.allow);
  const autoRec = new Set(data.rules.autoRecord);
  const allowlist = s.followMode === 'allowlist';
  const premiumHref = `${base}/premium`;

  return (
    <div class="stack">
      <Card title="When to join">
        <div class="choices" role="radiogroup" aria-label="When to join">
          {FOLLOW_MODES.map((m) => (
            <label key={m.id} class={`choice ${s.followMode === m.id ? 'selected' : ''}`}>
              <input type="radio" name="follow" checked={s.followMode === m.id} onChange={() => save(() => api.saveSettings(id, { followMode: m.id }))} />
              <span class="choice-title">{m.title}</span>
              <span class="choice-hint">{m.hint}</span>
            </label>
          ))}
        </div>
        <Field label="People needed before it joins" hint="Bots don't count. Once it's in, it stays while anyone is there. /join ignores this.">
          <select value={String(s.minHumans)} onChange={(e) => save(() => api.saveSettings(id, { minHumans: Number(e.currentTarget.value) }))}>
            {Array.from({ length: 10 }, (_, n) => n + 1).map((n) => (
              <option key={n} value={String(n)}>
                {n === 1 ? '1 person' : `${n} people`}
              </option>
            ))}
          </select>
        </Field>
        <p class="muted small">Changes to when it joins apply the next time a listener joins. Blocking a channel makes it leave right away.</p>
      </Card>

      <Card title="Voice channels">
        {data.guild.voiceChannels.length === 0 ? (
          <p class="muted">The bot can't see any voice channels in this server.</p>
        ) : (
          <div class="table-wrap">
            <table class="rules">
              <thead>
                <tr>
                  <th>Channel</th>
                  <th>Never join</th>
                  {allowlist && <th>Auto-follow</th>}
                  <th>
                    Auto-record {!plan.autoRecord && <PremiumBadge href={premiumHref} />}
                  </th>
                </tr>
              </thead>
              <tbody>
                {groupByCategory(data.guild.voiceChannels).map(([category, channels]) => [
                  category && (
                    <tr key={`cat-${category}`} class="group">
                      <td colSpan={allowlist ? 4 : 3}>{category}</td>
                    </tr>
                  ),
                  ...channels.map((c) => (
                    <tr key={c.id} class={block.has(c.id) ? 'blocked' : ''}>
                      <td class="channel">
                        <span aria-hidden="true">🔊</span> {c.name}
                      </td>
                      <td>
                        <Toggle checked={block.has(c.id)} label={`Never join ${c.name}`} onChange={(on) => save(() => api.setChannelRule(id, c.id, { block: on }))} />
                      </td>
                      {allowlist && (
                        <td>
                          <Toggle
                            checked={allow.has(c.id)}
                            disabled={block.has(c.id)}
                            label={`Auto-follow into ${c.name}`}
                            onChange={(on) => save(() => api.setChannelRule(id, c.id, { allow: on }))}
                          />
                        </td>
                      )}
                      <td>
                        <Toggle
                          checked={autoRec.has(c.id)}
                          // A rule kept from a past Premium can still be turned off.
                          disabled={block.has(c.id) || (!plan.autoRecord && !autoRec.has(c.id))}
                          label={`Auto-record ${c.name}`}
                          onChange={(on) => save(() => api.setChannelRule(id, c.id, { autoRecord: on }))}
                        />
                      </td>
                    </tr>
                  )),
                ])}
              </tbody>
            </table>
          </div>
        )}
        {allowlist && allow.size === 0 && <Notice kind="warn">No channel is allowed yet, so the listener only joins with /join or /move.</Notice>}
        <p class="muted small">Auto-record starts a full recording as soon as a listener joins that channel, with the usual recording notice.</p>
        <Status error={error} toast={toast} />
      </Card>

      <Card title="Notices">
        <Field label="Where join and recording notices go" hint="The bot always announces itself and every recording, so people know. You choose where.">
          <select value={s.announceChannelId ?? ''} onChange={(e) => save(() => api.saveSettings(id, { announceChannelId: e.currentTarget.value || null }))}>
            <option value="">Each voice channel's own chat</option>
            {data.guild.textChannels.map((c) => (
              <option key={c.id} value={c.id}>
                #{c.name}
              </option>
            ))}
          </select>
        </Field>
      </Card>

      <ExcludedRoles data={data} setData={setData} />
    </div>
  );
}

function ExcludedRoles({ data, setData }: { data: GuildData; setData: SetData }) {
  const { save, error, toast, busy } = useSaver(setData);
  const [picked, setPicked] = useState(() => new Set(data.excludedRoles));
  useEffect(() => setPicked(new Set(data.excludedRoles)), [data.excludedRoles]);
  const dirty = picked.size !== data.excludedRoles.length || data.excludedRoles.some((r) => !picked.has(r));
  const flip = (id: string, on: boolean) => {
    const next = new Set(picked);
    if (on) next.add(id);
    else next.delete(id);
    setPicked(next);
  };
  return (
    <Card
      title="Never capture these roles"
      actions={
        <button type="button" class="btn primary small" disabled={!dirty || busy} onClick={() => save(() => api.setExcludedRoles(data.guild.id, [...picked]))}>
          Save
        </button>
      }
    >
      <p class="muted small">Members with any of these roles are never in the replay buffer, clips or recordings in this server, like /optout just for here.</p>
      {data.guild.roles.length === 0 ? (
        <p class="muted">This server has no roles to pick from.</p>
      ) : (
        <ul class="role-list">
          {data.guild.roles.map((r) => (
            <li key={r.id}>
              <label class="check">
                <input type="checkbox" checked={picked.has(r.id)} onChange={(e) => flip(r.id, e.currentTarget.checked)} />
                <span class="role-dot" style={{ background: r.color ?? 'var(--muted)' }} aria-hidden="true" />
                {r.name}
              </label>
            </li>
          ))}
        </ul>
      )}
      <Status error={error} toast={toast} />
    </Card>
  );
}

// ---- Recording & clips ----

type NumericKey = 'bufferSeconds' | 'maxRecordMinutes' | 'retentionDays';

function RecordingTab({ data, setData, base }: TabProps) {
  const { save, error, toast, busy } = useSaver(setData);
  const plan = data.plan;
  const premium = data.plans.premium;
  const premiumHref = `${base}/premium`;
  const initial = () => ({ ...data.settings });
  const [form, setForm] = useState<Settings>(initial);
  useEffect(() => setForm(initial()), [data.settings]);

  const keys: (keyof Settings)[] = [
    'bufferSeconds',
    'maxRecordMinutes',
    'retentionDays',
    'cameraEnabled',
    'transcripts',
    'talkStats',
    'voiceClips',
    'clipSound',
    'clipChannelId',
    'transcriptLanguage',
    'recapChannelId',
  ];
  // /config can also pick a voice channel's chat, which isn't in the list.
  const clipChannelElsewhere = form.clipChannelId && !data.guild.textChannels.some((c) => c.id === form.clipChannelId) ? form.clipChannelId : null;
  const changed = keys.filter((k) => form[k] !== data.settings[k]);
  const set = <K extends keyof Settings>(k: K, v: Settings[K]) => setForm({ ...form, [k]: v });
  const submit = (e: JSX.TargetedEvent<HTMLFormElement>) => {
    e.preventDefault();
    void save(() => api.saveSettings(data.guild.id, Object.fromEntries(changed.map((k) => [k, form[k]])) as Partial<Settings>));
  };

  /** Explains a stored value above what the plan allows (kept for when Premium comes back). */
  const capNote = (k: NumericKey, max: number, unit: string): ComponentChildren =>
    form[k] > max ? (
      <Notice kind="warn">
        Set to {form[k]} {unit}, but the {plan.name} plan uses {max} {unit}. <Link href={premiumHref}>Premium</Link> goes up to{' '}
        {premium[k === 'bufferSeconds' ? 'maxBufferSeconds' : k === 'maxRecordMinutes' ? 'maxRecordMinutes' : 'maxRetentionDays']} {unit}.
      </Notice>
    ) : null;

  return (
    <form class="stack" onSubmit={submit}>
      <Card title="Instant replay">
        <Field label="Replay buffer" hint={`How far back /clip can reach. ${plan.name} plan: up to ${plan.maxBufferSeconds} seconds.`}>
          <NumberInput value={form.bufferSeconds} min={10} max={Math.max(plan.maxBufferSeconds, data.settings.bufferSeconds)} unit="seconds" onChange={(n) => set('bufferSeconds', n)} />
        </Field>
        {capNote('bufferSeconds', plan.maxBufferSeconds, 'seconds')}
        <Field label="Per-speaker tracks in /clip" hint="/clip per_user adds a zip with one file per speaker." locked={plan.perUserClips ? undefined : premiumHref}>
          <span class="muted">{plan.perUserClips ? 'Included' : 'Not included'}</span>
        </Field>
      </Card>

      <Card title="Recordings">
        <Field label="Longest recording" hint={`/record stops on its own after this. ${plan.name} plan: up to ${plan.maxRecordMinutes} minutes.`}>
          <NumberInput value={form.maxRecordMinutes} min={5} max={Math.max(plan.maxRecordMinutes, data.settings.maxRecordMinutes)} unit="minutes" onChange={(n) => set('maxRecordMinutes', n)} />
        </Field>
        {capNote('maxRecordMinutes', plan.maxRecordMinutes, 'minutes')}
        <Field label="Keep recordings and clips for" hint={`Older ones are deleted automatically. ${plan.name} plan: up to ${plan.maxRetentionDays} days.`}>
          <NumberInput value={form.retentionDays} min={1} max={Math.max(plan.maxRetentionDays, data.settings.retentionDays)} unit="days" onChange={(n) => set('retentionDays', n)} />
        </Field>
        {capNote('retentionDays', plan.maxRetentionDays, 'days')}
        <Field label="Per-speaker downloads" hint="Download each person's track, or all of them as a zip, from the web player." locked={plan.perSpeakerDownloads ? undefined : premiumHref}>
          <span class="muted">{plan.perSpeakerDownloads ? 'Included' : 'Mix only'}</span>
        </Field>
        {data.cameraCaptureAvailable && (
          <Field label="Record cameras (experimental)" hint="Cameras that are on are recorded during /record, with a notice." locked={plan.cameras ? undefined : premiumHref}>
            <Toggle checked={form.cameraEnabled && plan.cameras} disabled={!plan.cameras} label="Record cameras" onChange={(on) => set('cameraEnabled', on)} />
          </Field>
        )}
      </Card>

      <Card title="Extras">
        {!data.speech.available && <Notice kind="warn">This bot host has no speech-to-text set up, so transcripts and "clip that" are off for now.</Notice>}
        <Field
          label={data.speech.summaries ? 'Transcripts and AI summaries' : 'Transcripts'}
          hint={`/clip clips are transcribed${data.speech.summaries ? ' and summarized by Claude (Anthropic)' : ''}, and so are recordings stopped with /record stop transcribe:true. "Clip that" clips never are.`}
          locked={plan.transcripts ? undefined : premiumHref}
        >
          <Toggle
            checked={form.transcripts && plan.transcripts}
            disabled={!data.speech.available || !plan.transcripts}
            label="Transcripts"
            onChange={(on) => set('transcripts', on)}
          />
        </Field>
        <Field
          label="Transcript language"
          hint={
            data.speech.englishOnly
              ? "This bot's speech model only understands English. Its owner can switch models with npm run whisper."
              : 'The language people speak in your calls. Picking it is faster and more accurate than detecting it.'
          }
        >
          <select value={form.transcriptLanguage ?? ''} disabled={!data.speech.available || data.speech.englishOnly} onChange={(e) => set('transcriptLanguage', e.currentTarget.value || null)}>
            <option value="">The bot's default</option>
            {TRANSCRIPT_LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label='Say "clip that" to clip' hint="Anyone in the call can say it to save the last moments, no command needed.">
          <Toggle checked={form.voiceClips} disabled={!data.speech.available} label="Clip by voice" onChange={(on) => set('voiceClips', on)} />
        </Field>
        <Field label='Where "clip that" clips go' hint="Clips made by voice are posted in this channel. /clip still answers where it's used.">
          <select value={form.clipChannelId ?? ''} onChange={(e) => set('clipChannelId', e.currentTarget.value || null)}>
            <option value="">Where notices go</option>
            {clipChannelElsewhere && <option value={clipChannelElsewhere}>#{channelName(data, clipChannelElsewhere)}</option>}
            {data.guild.textChannels.map((c) => (
              <option key={c.id} value={c.id}>
                #{c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Sound when a clip is made" hint="Plays a short camera-shutter sound in the call after /clip or &quot;clip that&quot;, so everyone hears it. Needs the Speak permission.">
          <Toggle
            checked={form.clipSound && data.clipSoundAvailable}
            disabled={!data.clipSoundAvailable}
            label="Sound when a clip is made"
            onChange={(on) => set('clipSound', on)}
          />
        </Field>
        <Field label="Talk-time stats" hint="Keeps how long each person talks per day, for /stats. No audio is kept.">
          <Toggle checked={form.talkStats} label="Talk-time stats" onChange={(on) => set('talkStats', on)} />
        </Field>
        <Field label="Play saved clips in the call" hint="/play clip:<name> plays any of this server's clips to everyone in the call." locked={plan.playClips ? undefined : premiumHref}>
          <span class="muted">{plan.playClips ? 'Included' : 'Not included'}</span>
        </Field>
      </Card>

      <Card title="Weekly recap">
        <Field
          label="Post a weekly recap in"
          hint="Once a week: the week's clips (kept ones first), who talked most and the recordings made. /recap posts one now."
          locked={plan.weeklyRecap ? undefined : premiumHref}
        >
          <select value={form.recapChannelId ?? ''} disabled={!plan.weeklyRecap && !data.settings.recapChannelId} onChange={(e) => set('recapChannelId', e.currentTarget.value || null)}>
            <option value="">No recap</option>
            {data.guild.textChannels.map((c) => (
              <option key={c.id} value={c.id}>
                #{c.name}
              </option>
            ))}
          </select>
        </Field>
      </Card>

      <div class="savebar">
        <button type="submit" class="btn primary" disabled={changed.length === 0 || busy}>
          Save changes
        </button>
        {changed.length > 0 && (
          <button type="button" class="btn ghost" onClick={() => setForm(initial())}>
            Undo
          </button>
        )}
        <Status error={error} toast={toast} />
      </div>
    </form>
  );
}

// ---- Library ----

function Library({ data }: TabProps) {
  const id = data.guild.id;
  const [recs, setRecs] = useState<RecordingItem[] | null>(null);
  const [recNext, setRecNext] = useState<number | null>(null);
  const [retention, setRetention] = useState<number | null>(null);
  const [clips, setClips] = useState<ClipItem[] | null>(null);
  const [clipNext, setClipNext] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadRecs = async (before?: number) => {
    try {
      const page = await api.recordings(id, before);
      setRecs((old) => (before && old ? [...old, ...page.items] : page.items));
      setRecNext(page.next);
      setRetention(page.retentionDays);
    } catch (err) {
      setError((err as Error).message);
    }
  };
  const loadClips = async (before?: number) => {
    try {
      const page = await api.clips(id, before);
      setClips((old) => (before && old ? [...old, ...page.items] : page.items));
      setClipNext(page.next);
    } catch (err) {
      setError((err as Error).message);
    }
  };
  useEffect(() => {
    void loadRecs();
    void loadClips();
  }, [id]);

  const removeRec = async (r: RecordingItem) => {
    if (!confirm(`Delete the recording from #${r.channelName ?? 'voice'} on ${fmtDate(r.startedAt)}? This can't be undone.`)) return;
    try {
      await api.deleteRecording(id, r.id);
      setRecs((old) => old?.filter((x) => x.id !== r.id) ?? null);
    } catch (err) {
      setError((err as Error).message);
    }
  };
  const removeClip = async (c: ClipItem) => {
    if (!confirm(`Delete the clip "${c.title ?? 'Clip'}"? This can't be undone.`)) return;
    try {
      await api.deleteClip(id, c.id);
      setClips((old) => old?.filter((x) => x.id !== c.id) ?? null);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div class="stack">
      {error && <Notice kind="error">{error}</Notice>}
      <Card title="Recordings" actions={retention !== null && <span class="muted small">Kept for {plural(retention, 'day')}</span>}>
        {!recs ? (
          <Spinner />
        ) : recs.length === 0 ? (
          <p class="muted">No recordings yet. Run /record start in a call{data.plan.autoRecord ? ', or turn on auto-record for a channel' : ''}.</p>
        ) : (
          <ul class="items">
            {recs.map((r) => (
              <li key={r.id} class="item">
                <div class="item-main">
                  <div class="item-title">
                    #{r.channelName ?? 'voice'} · {fmtDate(r.startedAt)}
                  </div>
                  <div class="muted small">
                    {r.durationMs !== null ? fmtDuration(r.durationMs) : 'In progress'} · {r.speakers.length ? r.speakers.join(', ') : 'no speakers'} ·{' '}
                    {r.auto ? 'auto-record' : `started by ${r.startedByName ?? 'someone'}`}
                  </div>
                  {r.status === 'failed' && <div class="error-text small">Couldn't be processed: {r.error ?? 'unknown error'}</div>}
                </div>
                <div class="row-actions">
                  {r.status === 'ready' && r.url ? (
                    <a class="btn small" href={r.url} target="_blank" rel="noopener">
                      Open player
                    </a>
                  ) : (
                    <span class="badge">{r.status === 'failed' ? 'Failed' : r.status === 'recording' ? 'Recording' : 'Processing'}</span>
                  )}
                  {(r.status === 'ready' || r.status === 'failed') && (
                    <button type="button" class="btn small ghost danger" onClick={() => removeRec(r)}>
                      Delete
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        {recNext !== null && (
          <button type="button" class="btn ghost" onClick={() => loadRecs(recNext)}>
            Load more
          </button>
        )}
      </Card>

      <Card title="Clips">
        {!clips ? (
          <Spinner />
        ) : clips.length === 0 ? (
          <p class="muted">No clips yet. Run /clip in a call to save the last moments.</p>
        ) : (
          <ul class="items">
            {clips.map((c) => (
              <ClipRow
                key={c.id}
                guildId={id}
                clip={c}
                keptLimit={data.plan.maxKeptClips}
                onSaved={(saved) => setClips((old) => old?.map((x) => (x.id === saved.id ? saved : x)) ?? null)}
                onDelete={removeClip}
              />
            ))}
          </ul>
        )}
        {clipNext !== null && (
          <button type="button" class="btn ghost" onClick={() => loadClips(clipNext)}>
            Load more
          </button>
        )}
      </Card>
    </div>
  );
}

/** Same limits as the server (CLIP_TITLE_MAX, CLIP_DESCRIPTION_MAX). */
const CLIP_TITLE_MAX = 80;
const CLIP_DESCRIPTION_MAX = 500;

/** One clip at a time: starting one pauses whichever was playing. */
function pauseOthers(e: JSX.TargetedEvent<HTMLAudioElement>) {
  for (const a of document.querySelectorAll('audio')) if (a !== e.currentTarget) a.pause();
}

function ClipRow({
  guildId,
  clip,
  keptLimit,
  onSaved,
  onDelete,
}: {
  guildId: string;
  clip: ClipItem;
  keptLimit: number;
  onSaved: (c: ClipItem) => void;
  onDelete: (c: ClipItem) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startEditing = () => {
    setTitle(clip.title ?? '');
    setDescription(clip.description ?? '');
    setError(null);
    setEditing(true);
  };
  const submit = async (e: JSX.TargetedEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSaved(await api.updateClip(guildId, clip.id, { title: title.trim() || null, description: description.trim() || null }));
      setEditing(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const quiet = clip.participants.filter((p) => !p.spoke).length;
  const toggleKept = async () => {
    setBusy(true);
    setError(null);
    try {
      onSaved(await api.updateClip(guildId, clip.id, { kept: !clip.kept }));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li class="item clip-row">
      <div class="item-main">
        {editing ? (
          <form class="clip-edit" onSubmit={submit}>
            <label>
              <span class="small muted">Name</span>
              <input value={title} maxLength={CLIP_TITLE_MAX} placeholder="Clip" onInput={(e) => setTitle(e.currentTarget.value)} autoFocus />
            </label>
            <label>
              <span class="small muted">Description</span>
              <textarea value={description} maxLength={CLIP_DESCRIPTION_MAX} rows={3} placeholder="What happened?" onInput={(e) => setDescription(e.currentTarget.value)} />
            </label>
            <div class="row-actions">
              <button type="submit" class="btn primary small" disabled={busy}>
                Save
              </button>
              <button type="button" class="btn ghost small" disabled={busy} onClick={() => setEditing(false)}>
                Cancel
              </button>
            </div>
            {error && <Notice kind="error">{error}</Notice>}
          </form>
        ) : (
          <>
            <div class="item-title">
              {clip.title ?? 'Clip'} {clip.kept && <span class="badge kept">⭐ Kept</span>}
            </div>
            {clip.description && <p class="clip-description">{clip.description}</p>}
            {error && <Notice kind="error">{error}</Notice>}
          </>
        )}
        <div class="muted small">
          {fmtAgo(clip.createdAt)} · {clip.seconds}s{clip.channelName ? ` · #${clip.channelName}` : ''} · by {clip.createdByName ?? 'someone'}
        </div>
        {clip.participants.length > 0 && (
          <div class="clip-people">
            <People people={clip.participants} size={28} />
            <span class="muted small">
              {plural(clip.participants.length, 'person')}
              {quiet ? ` · ${quiet} didn't speak` : ''}
            </span>
          </div>
        )}
        <audio class="clip-audio" controls preload="none" src={clip.audioUrl} onPlay={pauseOthers} />
      </div>
      <div class="row-actions">
        {!editing && (
          <button type="button" class="btn small" onClick={startEditing}>
            Name & describe
          </button>
        )}
        <button
          type="button"
          class={`btn small ${clip.kept ? 'primary' : ''}`}
          disabled={busy}
          aria-pressed={clip.kept}
          title={clip.kept ? 'Kept forever. Click to let it expire like other clips.' : `Keep forever, so it's never cleaned up (up to ${keptLimit} clips).`}
          onClick={() => void toggleKept()}
        >
          {clip.kept ? '⭐ Kept' : '☆ Keep'}
        </button>
        <a class="btn small" href={clip.pageUrl} target="_blank" rel="noopener">
          Clip page
        </a>
        <a class="btn small" href={clip.url}>
          Download
        </a>
        <button type="button" class="btn small ghost danger" onClick={() => onDelete(clip)}>
          Delete
        </button>
      </div>
    </li>
  );
}

// ---- Premium ----

function PremiumTab({ data, reload }: TabProps) {
  const plan = data.plan;
  const free = data.plans.free;
  const prem = data.plans.premium;
  const checkout = new URLSearchParams(location.search).get('checkout');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Paddle tells the bot through a webhook, which can take a few seconds after paying.
  useEffect(() => {
    if (checkout !== 'success' || plan.id === 'premium') return;
    let n = 0;
    const t = setInterval(() => {
      if (++n > 15) clearInterval(t);
      void reload();
    }, 2000);
    return () => clearInterval(t);
  }, [checkout, plan.id, reload]);

  const go = async (fn: () => Promise<{ url: string }>) => {
    setBusy(true);
    setError(null);
    try {
      const { url } = await fn();
      // The checkout page sends the buyer back to this server's Premium tab when they're done.
      try {
        sessionStorage.setItem('checkoutGuild', data.guild.id);
      } catch {
        /* private mode: they land on the server list instead */
      }
      location.href = url;
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  const sub = plan.subscription;
  const yes = <span class="yes">✓</span>;
  const no = <span class="no">—</span>;
  const rows: [string, ComponentChildren, ComponentChildren][] = [
    ['Voice channels at once', free.maxListeners, prem.maxListeners],
    ['Instant replay', `${free.maxBufferSeconds} s`, `${prem.maxBufferSeconds / 60} min`],
    ['Per-speaker zip in /clip', no, yes],
    ['Longest recording', `${free.maxRecordMinutes} min`, `${prem.maxRecordMinutes / 60} h`],
    ['Keep recordings and clips', `${free.maxRetentionDays} days`, `${prem.maxRetentionDays} days`],
    ['Web player and mix download', yes, yes],
    ['Per-speaker downloads and exports', no, yes],
    ['Auto-record chosen channels', no, yes],
    ...(data.speech.available
      ? [[data.speech.summaries ? 'Transcripts and AI summaries' : 'Transcripts', no, yes] as [string, ComponentChildren, ComponentChildren]]
      : []),
    ...(data.cameraCaptureAvailable ? [['Camera recording (experimental)', no, yes] as [string, ComponentChildren, ComponentChildren]] : []),
    ...(data.speech.available ? [['Say "clip that" to clip', yes, yes] as [string, ComponentChildren, ComponentChildren]] : []),
    ['Clips kept forever (⭐)', free.maxKeptClips, prem.maxKeptClips],
    ['Play saved clips in the call (/play)', no, yes],
    ['Weekly recap', no, yes],
    ['Talk-time stats (/stats)', yes, yes],
    ['Dashboard, channel rules and excluded roles', yes, yes],
  ];

  return (
    <div class="stack">
      {checkout === 'success' &&
        (plan.id === 'premium' ? (
          <Notice kind="success">Premium is active. Thank you for supporting the bot!</Notice>
        ) : (
          <Notice>Payment received. Turning on Premium, this takes a few seconds…</Notice>
        ))}
      {checkout === 'cancelled' && <Notice>Checkout was cancelled and nothing was charged.</Notice>}
      {plan.graceEndsAt && (
        <Notice kind="warn">
          Premium has ended. Recordings and clips older than {free.maxRetentionDays} days will be deleted after {fmtDate(plan.graceEndsAt, false)}. Download
          anything you want to keep, or resubscribe.
        </Notice>
      )}

      <Card title={plan.id === 'premium' ? 'This server has Premium' : 'This server is on the Free plan'}>
        {plan.id === 'premium' ? (
          plan.comped ? (
            <p>Premium was granted to this server by the bot operator.</p>
          ) : sub ? (
            <div class="stack-sm">
              {sub.status === 'past_due' && <Notice kind="warn">The last payment didn't go through. Paddle will retry; updating the payment method fixes it.</Notice>}
              <p>
                {sub.status === 'trialing'
                  ? `Free trial until ${sub.currentPeriodEnd ? fmtDate(sub.currentPeriodEnd, false) : 'soon'}.`
                  : sub.cancelAtPeriodEnd
                    ? `Cancelled. Premium stays on until ${sub.currentPeriodEnd ? fmtDate(sub.currentPeriodEnd, false) : 'the end of the period'}.`
                    : `Renews on ${sub.currentPeriodEnd ? fmtDate(sub.currentPeriodEnd, false) : 'its next billing date'}.`}{' '}
                Paid by {sub.isPurchaser ? 'you' : (sub.purchasedByName ?? 'another manager')}.
              </p>
              {sub.isPurchaser ? (
                <div>
                  <button type="button" class="btn primary" disabled={busy} onClick={() => go(() => api.portal(data.guild.id))}>
                    Manage billing
                  </button>
                  <p class="muted small">Change your payment method, download invoices or cancel, on Paddle.</p>
                </div>
              ) : (
                <p class="muted small">Only the person who pays can change billing.</p>
              )}
            </div>
          ) : (
            <p>Every feature is unlocked on this bot.</p>
          )
        ) : plan.billingEnabled ? (
          <div class="stack-sm">
            <p>Premium is per server: one subscription unlocks it for everyone here.</p>
            <div class="buy">
              <button type="button" class="btn primary big" disabled={busy} onClick={() => go(() => api.checkout(data.guild.id, 'month'))}>
                Upgrade, billed monthly
              </button>
              {plan.prices.year && (
                <button type="button" class="btn big" disabled={busy} onClick={() => go(() => api.checkout(data.guild.id, 'year'))}>
                  Upgrade, billed yearly
                </button>
              )}
            </div>
            <p class="muted small">
              You'll see the price, with any tax, before paying. Paddle handles the payment and invoice as the reseller; cancel any time.
            </p>
          </div>
        ) : (
          <p class="muted">Premium isn't sold on this bot. Ask the bot operator.</p>
        )}
        {error && <Notice kind="error">{error}</Notice>}
      </Card>

      <Card title="Free and Premium">
        <div class="table-wrap">
          <table class="compare">
            <thead>
              <tr>
                <th />
                <th>Free</th>
                <th>
                  <PremiumBadge />
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([label, a, b]) => (
                <tr key={label}>
                  <th scope="row">{label}</th>
                  <td>{a}</td>
                  <td>{b}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p class="muted small">Consent features are the same on both: join and recording notices, the 🔴 nickname indicator, /optout and excluded roles.</p>
      </Card>
    </div>
  );
}

// ---- Activity ----

const SETTING_LABELS: Record<string, string> = {
  bufferSeconds: 'replay buffer',
  announceChannelId: 'notices channel',
  retentionDays: 'days kept',
  maxRecordMinutes: 'longest recording',
  followMode: 'when to join',
  minHumans: 'people needed to join',
  cameraEnabled: 'camera recording',
  transcripts: 'transcripts',
  talkStats: 'talk-time stats',
  voiceClips: '"clip that"',
  clipSound: 'clip sound',
  clipChannelId: '"clip that" channel',
  transcriptLanguage: 'transcript language',
  recapChannelId: 'weekly recap channel',
};

function describe(data: GuildData, item: AuditItem): string {
  const d = (item.detail ?? {}) as Record<string, unknown>;
  const ch = (id: unknown) => `#${channelName(data, typeof id === 'string' ? id : null)}`;
  const value = (key: string, v: unknown): string => {
    if (v === null) {
      if (key === 'clipChannelId') return 'where notices go';
      if (key === 'recapChannelId') return 'off';
      if (key === 'transcriptLanguage') return "the bot's default";
      return "each voice channel's chat";
    }
    switch (key) {
      case 'announceChannelId':
      case 'clipChannelId':
      case 'recapChannelId':
        return ch(v);
      case 'transcriptLanguage':
        return languageName(String(v));
      case 'followMode':
        return FOLLOW_SHORT[v as FollowMode]?.toLowerCase() ?? String(v);
      case 'bufferSeconds':
        return `${String(v)}s`;
      case 'retentionDays':
        return `${String(v)} days`;
      case 'maxRecordMinutes':
        return `${String(v)} min`;
      case 'cameraEnabled':
      case 'transcripts':
      case 'talkStats':
      case 'voiceClips':
      case 'clipSound':
        return v ? 'on' : 'off';
      default:
        return String(v);
    }
  };
  switch (item.action) {
    case 'settings':
      return `Changed ${Object.entries(d)
        .map(([k, pair]) => {
          const [from, to] = pair as [unknown, unknown];
          return `${SETTING_LABELS[k] ?? k} from ${value(k, from)} to ${value(k, to)}`;
        })
        .join(', ')}`;
    case 'channel_rule': {
      const what = d.rule === 'block' ? (d.on ? 'Blocked' : 'Unblocked') : d.rule === 'allow' ? (d.on ? 'Allowed auto-follow into' : 'Stopped auto-following into') : d.on ? 'Turned on auto-record for' : 'Turned off auto-record for';
      return `${what} ${ch(d.channelId)}`;
    }
    case 'excluded_roles': {
      const ids = Array.isArray(d.roleIds) ? (d.roleIds as string[]) : [];
      const names = ids.map((r) => `@${data.guild.roles.find((x) => x.id === r)?.name ?? 'deleted role'}`);
      return names.length ? `Set roles that are never captured: ${names.join(', ')}` : 'Cleared the roles that are never captured';
    }
    case 'recording_deleted':
      return `Deleted a recording from #${String(d.channelName ?? 'voice')}${typeof d.startedAt === 'number' ? ` (${fmtDate(d.startedAt)})` : ''}`;
    case 'clip_deleted':
      return `Deleted the clip "${String(d.title ?? 'Clip')}"`;
    case 'clip_kept':
      return `${d.kept ? 'Kept' : 'Stopped keeping'} the clip "${String(d.title ?? 'Clip')}"${d.kept ? ' forever' : ''}`;
    case 'clip_edited':
      return d.from === d.title
        ? `Changed the description of the clip "${String(d.title ?? 'Clip')}"`
        : `Renamed the clip "${String(d.from ?? 'Clip')}" to "${String(d.title ?? 'Clip')}"`;
    case 'left_channel':
      return `Made the listener leave ${ch(d.channelId)}`;
    case 'stopped_recording':
      return `Stopped the recording in ${ch(d.channelId)}`;
    case 'billing':
      return d.event === 'checkout_started' ? 'Opened Premium checkout' : d.event === 'premium_started' ? 'Premium started' : d.event === 'premium_ended' ? 'Premium ended' : 'Billing changed';
    default:
      return item.action;
  }
}

function Activity({ data }: TabProps) {
  const [items, setItems] = useState<AuditItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.audit(data.guild.id).then(
      (r) => setItems(r.items),
      (err: Error) => setError(err.message),
    );
  }, [data.guild.id]);
  return (
    <Card title="Activity">
      <p class="muted small">Changes made from the dashboard and /config, newest first.</p>
      {error && <Notice kind="error">{error}</Notice>}
      {!items && !error ? (
        <Spinner />
      ) : items?.length === 0 ? (
        <p class="muted">Nothing has been changed yet.</p>
      ) : (
        <ul class="items">
          {items?.map((it, n) => (
            <li key={`${it.at}-${n}`} class="item">
              <div class="item-main">
                <div>{describe(data, it)}</div>
                <div class="muted small">
                  {it.userName ?? (it.action === 'billing' ? 'Billing' : 'Someone')} · {fmtDate(it.at)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
